import sortBy from 'lodash/sortBy';

import { useCompleteFragment } from '@eluve/apollo-client';

import { useAppointmentId } from './appointment.context';
import {
  appointmentSegmentsFragment,
  appointmentTranscriptAndSegmentsFragment,
} from './operations';

/**
 * Gives convenient access to the full concatenated transcript for an appointment
 * by reading it from the apollo cache
 */
export const useAppointmentTranscript = () => {
  const id = useAppointmentId();

  const data = useCompleteFragment({
    fragment: appointmentTranscriptAndSegmentsFragment,
    key: {
      id,
    },
    strict: false,
  });

  const segments = sortBy(
    (data?.segments ?? []).map((s) => ({
      transcript: s.transcript ?? '',
      startedAt: s.recordingStartedAt,
      pausedAt: s.recordingDuration
        ? new Date(
            new Date(s.recordingStartedAt).getTime() + s.recordingDuration,
          ).toISOString()
        : undefined,
    })),
    (s) => s.startedAt,
  );

  return {
    transcript: data?.transcription?.transcript ?? '',
    segments,
  };
};

/**
 * Total transcript length (chars) across the appointment's segments, by explicit
 * id (context-free, safe in the floating widget). Reads the segments fragment —
 * not the concatenated transcript — so it updates reactively after an insert.
 */
export const useAppointmentTranscriptLength = (
  appointmentId: string,
): number => {
  const data = useCompleteFragment({
    fragment: appointmentSegmentsFragment,
    key: { id: appointmentId },
    strict: false,
  });

  return (data?.segments ?? []).reduce(
    (total, segment) => total + (segment.transcript?.trim().length ?? 0),
    0,
  );
};
